import { useIntl } from 'react-intl';
import { useMutation } from '@tanstack/react-query';

import { API } from 'src/reactQuery/Api';
import { putRequest } from 'src/utils/request';
import useMessenger from 'src/utils/hooks/useMessenger';
import { RequestSimplifiedData } from 'src/types/requestData';
import messages from 'src/pages/ProjectDetailsPage/messages';
import { useMessageHandler } from 'src/utils/hooks/useMessageHandler';

import { useDeleteJobFields } from './useDeleteJobFields';
import { useCreateJobFields, type JobFieldType } from './useCreateJobFields';

import { buildSaveJobParams, type SaveJobParamsType } from './useCreateJob';

interface UpdateJobParamsType extends SaveJobParamsType {
  jobUuid: string;
  fields: Array<JobFieldType>;
  fieldsToRemove: Array<Record<'fieldUuid', string>>;
}

const updateJob = async (props: UpdateJobParamsType) => {
  const { data } = await putRequest<RequestSimplifiedData>(
    API.PUT.updateJob(props.jobUuid),
    buildSaveJobParams(props),
  );

  return data;
};

export const useUpdateJob = () => {
  const messenger = useMessenger();
  const { formatMessage } = useIntl();
  const { handleError } = useMessageHandler();

  const { mutate: createJobFields } = useCreateJobFields();
  const { mutate: deleteJobFields } = useDeleteJobFields();

  return useMutation({
    mutationFn: updateJob,
    onSuccess: ({ data: { uuid } }, { fieldsToRemove, fields }) => {
      return deleteJobFields(
        { jobUuid: uuid, fieldsToRemove },
        {
          onSuccess: () => {
            return createJobFields(
              { jobUuid: uuid, fields },
              {
                onError: () =>
                  messenger.warning(
                    formatMessage(messages.jobPartiallySaved),
                    1000,
                  ),
              },
            );
          },
        },
      );
    },
    onError: handleError,
  });
};
